interface GraphNodeValue {
  description?: unknown;
  entity?: unknown;
  fk?: unknown;
  hidden?: unknown;
  id?: unknown;
  value?: unknown;
}

interface ChildFieldMapping {
  targetField?: unknown;
}

/** Child-expansion fields that affect the shape of a delta mapping record. */
export interface DeltaChildExpansion {
  arrayField?: unknown;
  enabled?: unknown;
  fieldMappings?: unknown;
  mergeChildFields?: unknown;
}

/** Cursor context used to select and place Monaco completion items. */
export type DeltaCompletionContext =
  | {
      kind: "jsonKey" | "symbol";
      query: string;
      replaceStart: number;
    }
  | {
      kind: "recordDot";
      query: string;
      dotStart: number;
      replaceStart: number;
    }
  | {
      kind: "recordBracket" | "recordGet";
      query: string;
      replaceStart: number;
      needsClosingSyntax: boolean;
    };

interface OffsetRange {
  start: number;
  end: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeUniqueStrings = (values: unknown[]): string[] => {
  const uniqueValues = new Set<string>();
  for (const value of values) {
    if (typeof value !== "string") continue;
    const normalized = value.trim();
    if (normalized) uniqueValues.add(normalized);
  }
  return Array.from(uniqueValues);
};

const getGraphNodeValues = (graph: unknown): GraphNodeValue[] => {
  if (!isRecord(graph)) return [];

  if (Array.isArray(graph.nodes)) {
    return graph.nodes.map((node) => (isRecord(node) && isRecord(node.value) ? node.value : node)).filter(isRecord);
  }

  if (typeof graph.nodes !== "function" || typeof graph.node !== "function") return [];

  try {
    const nodeIds = (graph.nodes as () => unknown[])();
    return nodeIds.map((nodeId) => (graph.node as (id: unknown) => unknown)(nodeId)).filter(isRecord);
  } catch {
    return [];
  }
};

/** Returns readable attribute names for an entity, optionally including hidden calculated attributes. */
export const getDeltaEntityFieldNames = (
  graph: unknown,
  entity: string,
  options: { includeHidden?: boolean } = {},
): string[] =>
  normalizeUniqueStrings(
    getGraphNodeValues(graph)
      // Foreign keys link records to other entities; they aren't values a template reads.
      .filter((node) => node.entity === entity && !node.fk && (options.includeHidden === true || !node.hidden))
      .map((node) => node.description || node.id),
  );

const getChildFieldMappings = (childExpansion: DeltaChildExpansion | undefined): ChildFieldMapping[] => {
  if (!Array.isArray(childExpansion?.fieldMappings)) return [];
  return childExpansion.fieldMappings.filter(isRecord);
};

/**
 * Returns the readable keys available on `record` for a delta JSON-e template.
 * The selected entity contributes its attribute descriptions; child expansion
 * contributes explicit target fields or merged child attribute descriptions.
 */
export const getDeltaRecordFields = (
  graph: unknown,
  entity: string,
  childExpansion?: DeltaChildExpansion,
): string[] => {
  // Hidden (calculated) attributes are still on each record, so they are offered and accepted too.
  const fields: unknown[] = ["@id", ...getDeltaEntityFieldNames(graph, entity, { includeHidden: true })];

  if (childExpansion?.enabled === true) {
    fields.push(...getChildFieldMappings(childExpansion).map((mapping) => mapping.targetField));

    if (childExpansion.mergeChildFields === true && typeof childExpansion.arrayField === "string") {
      fields.push(...getDeltaEntityFieldNames(graph, childExpansion.arrayField, { includeHidden: true }));
    }
  }

  return normalizeUniqueStrings(fields).sort((left, right) => {
    if (left === "@id") return -1;
    if (right === "@id") return 1;
    return left.localeCompare(right);
  });
};

const getJsonStringRangeAtOffset = (text: string, offset: number): OffsetRange | null => {
  let inString = false;
  let escaped = false;
  let start = 0;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (!inString) {
      if (character === '"') {
        inString = true;
        start = index + 1;
      }
      continue;
    }

    if (escaped) {
      escaped = false;
      continue;
    }

    if (character === "\\") {
      escaped = true;
      continue;
    }

    if (character === '"') {
      if (offset >= start && offset <= index) return { start, end: index };
      inString = false;
    }
  }

  return inString && offset >= start ? { start, end: text.length } : null;
};

const getMatchStart = (text: string, match: RegExpMatchArray): number => text.length - match[0].length;

/**
 * Identifies the JSON-e expression under the cursor so Monaco can offer
 * context-specific variables, functions, and `record` fields.
 */
export const getDeltaCompletionContext = (text: string, offset: number): DeltaCompletionContext | null => {
  const stringRange = getJsonStringRangeAtOffset(text, offset);
  if (!stringRange) return null;

  const beforeCursor = text.slice(stringRange.start, offset);
  const afterCursor = text.slice(offset, stringRange.end);
  const beforeString = text.slice(0, stringRange.start - 1).trimEnd();
  const isJsonKey = beforeString.endsWith("{") || beforeString.endsWith(",");

  if (isJsonKey) {
    return {
      kind: "jsonKey",
      query: beforeCursor,
      replaceStart: stringRange.start,
    };
  }

  const bracketMatch = beforeCursor.match(/\brecord\[\s*'([^']*)$/);
  if (bracketMatch) {
    return {
      kind: "recordBracket",
      query: bracketMatch[1],
      replaceStart:
        stringRange.start + getMatchStart(beforeCursor, bracketMatch) + bracketMatch[0].lastIndexOf("'") + 1,
      needsClosingSyntax: !afterCursor.trimStart().startsWith("']"),
    };
  }

  const getMatch = beforeCursor.match(/\bget\(\s*record\s*,\s*'([^']*)$/);
  if (getMatch) {
    return {
      kind: "recordGet",
      query: getMatch[1],
      replaceStart: stringRange.start + getMatchStart(beforeCursor, getMatch) + getMatch[0].lastIndexOf("'") + 1,
      needsClosingSyntax: !afterCursor.trimStart().startsWith("')"),
    };
  }

  const dotMatch = beforeCursor.match(/\brecord\.([A-Za-z0-9_$]*)$/);
  if (dotMatch) {
    const matchStart = stringRange.start + getMatchStart(beforeCursor, dotMatch);
    const dotStart = matchStart + dotMatch[0].indexOf(".");
    return {
      kind: "recordDot",
      query: dotMatch[1],
      dotStart,
      replaceStart: dotStart + 1,
    };
  }

  const symbolMatch = beforeCursor.match(/(?:^|[\s(,])([A-Za-z_$][\w$]*)$/);
  if (!symbolMatch) return null;

  return {
    kind: "symbol",
    query: symbolMatch[1],
    replaceStart: stringRange.start + beforeCursor.length - symbolMatch[1].length,
  };
};

/** Returns whether a field can use JavaScript-style dot property access. */
export const isDotAccessibleField = (field: string): boolean => /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(field);

/** Escapes a field for insertion into a single-quoted JSON-e expression. */
export const escapeSingleQuotedField = (field: string): string =>
  field.replace(/\\/g, "\\\\\\\\").replace(/'/g, "\\\\'");
